import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Box, Paper, Typography, TextField, IconButton, Stack, CircularProgress, Chip, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
// components
import Iconify from '../../components/Iconify';
import ChatMessage from './ChatMessage';
// hooks
import useLocales from '../../hooks/useLocales';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { sendMessage, closeChat } from '../../redux/slices/chat';
// utils
import { requestNotificationPermission } from '../../utils/desktopNotification';

// ----------------------------------------------------------------------

// Written for LTR; the app's global stylis-plugin-rtl (see ThemeRtlLayout)
// automatically mirrors this to the left edge when the language is Arabic.
// Do not manually flip left/right here — that would double-mirror.
const ChatContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 90,
  right: 20,
  width: 380,
  height: 520,
  display: 'flex',
  flexDirection: 'column',
  zIndex: theme.zIndex.modal,
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: theme.shadows[20],
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100vw - 32px)',
    height: 'calc(100vh - 120px)',
    bottom: 80,
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

const MessagesContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
});

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
}));

// ----------------------------------------------------------------------

ChatWindow.propTypes = {
  open: PropTypes.bool,
};

export default function ChatWindow({ open }) {
  const { translate, currentLang } = useLocales();
  const dispatch = useDispatch();
  const { messages, isSending, isLoading } = useSelector((state) => state.chat);
  const [inputValue, setInputValue] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [notifPermission, setNotifPermission] = useState(() =>
    'Notification' in window ? Notification.permission : 'denied'
  );
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);
  const wasSendingRef = useRef(false);
  const isRtl = currentLang.direction === 'rtl';

  const handleEnableNotifications = async () => {
    await requestNotificationPermission();
    if ('Notification' in window) setNotifPermission(Notification.permission);
  };

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Scroll to bottom and focus input when window opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
        }
        textInputRef.current?.focus();
      }, 50);
    }
  }, [open]);

  // Restore focus when isSending goes true → false (after send completes)
  useEffect(() => {
    if (wasSendingRef.current && !isSending) {
      textInputRef.current?.focus();
    }
    wasSendingRef.current = isSending;
  }, [isSending]);

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text && selectedFiles.length === 0) return;
    setInputValue('');
    setSelectedFiles([]);
    dispatch(sendMessage(text, selectedFiles));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (!open) return null;

  return (
    <ChatContainer>
      <Header>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify icon="ic:baseline-support-agent" width={24} height={24} />
          <Typography variant="subtitle1" fontWeight="bold">
            {translate('chat.title')}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center">
          {notifPermission !== 'granted' && notifPermission !== 'denied' && (
            <Tooltip title={translate('chat.enableNotifications')}>
              <IconButton size="small" onClick={handleEnableNotifications} sx={{ color: 'inherit' }}>
                <Iconify icon="ic:baseline-notifications-none" width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => dispatch(closeChat())} sx={{ color: 'inherit' }}>
            <Iconify icon="eva:close-fill" width={20} height={20} />
          </IconButton>
        </Stack>
      </Header>

      <MessagesContainer>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            {messages.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {translate('chat.welcome')}
                </Typography>
              </Box>
            )}
            {messages.map((msg, index) => (
              <ChatMessage key={msg.id || index} message={msg} />
            ))}
            {isSending && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  {translate('chat.typing')}
                </Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </MessagesContainer>

      <InputContainer sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 0.5 }}>
        {selectedFiles.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pb: 0.5 }}>
            {selectedFiles.map((file, i) => (
              <Chip
                key={i}
                label={file.name.length > 20 ? `${file.name.slice(0, 18)}…` : file.name}
                size="small"
                onDelete={() => removeFile(i)}
              />
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
            <Iconify icon="ic:baseline-attach-file" width={20} height={20} />
          </IconButton>
          <TextField
            fullWidth
            size="small"
            placeholder={translate('chat.placeholder')}
            value={inputValue}
            inputRef={textInputRef}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            multiline
            maxRows={3}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={(!inputValue.trim() && selectedFiles.length === 0) || isSending}
            sx={{ flexShrink: 0 }}
          >
            {isSending ? (
              <CircularProgress size={20} />
            ) : (
              <Iconify
                icon="ic:baseline-send"
                width={24}
                height={24}
                sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }}
              />
            )}
          </IconButton>
        </Box>
      </InputContainer>
    </ChatContainer>
  );
}
